(() => {
  if (customElements.get('brl-product-services')) return;

  const money = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);

  const visible = (element) => {
    if (!element.getClientRects().length) return false;

    for (
      let node = element;
      node;
      node = node.parentElement || node.getRootNode()?.host
    ) {
      if (node.hasAttribute('hidden') || node.hasAttribute('inert')) return false;

      const style = getComputedStyle(node);

      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0'
      ) {
        return false;
      }
    }

    return true;
  };

  let afterpayScript;

  const loadAfterpay = () => {
    if (customElements.get('square-placement')) return Promise.resolve();

    if (afterpayScript) return afterpayScript;

    afterpayScript = new Promise((resolve, reject) => {
      const existing = document.querySelector(
        'script[src="https://js.squarecdn.com/square-marketplace.js"]'
      );

      const script = existing || document.createElement('script');

      const finish = (error) => {
        clearTimeout(timeout);

        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);

        if (error) {
          if (!existing) script.remove();

          afterpayScript = null;
          reject(error);
        } else {
          resolve();
        }
      };

      const onLoad = () => finish();
      const onError = () => finish(new Error('Afterpay did not load'));

      const timeout = setTimeout(onError, 15000);

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      if (!existing) {
        script.src = 'https://js.squarecdn.com/square-marketplace.js';
        script.async = true;
        document.head.append(script);
      }
    });

    return afterpayScript;
  };

  class BrlProductServices extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.controller = new AbortController();

      const options = {
        signal: this.controller.signal,
      };

      this.variants = JSON.parse(
        this.querySelector('[data-variants]').textContent
      );

      this.productInfo = this.closest('product-info');
      this.productForm = document.getElementById(this.dataset.formId);
      this.trigger = this.querySelector('[data-payment-trigger]');
      this.layer =
        this.layer || this.querySelector('[data-payment-layer]');
      this.panel = this.layer?.querySelector('.brl-payment-panel');
      this.closeButton = this.layer?.querySelector(
        '[data-payment-close]'
      );

      this.productInfo?.addEventListener(
        'change',
        () => this.syncFromForm(),
        options
      );

      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe('variant-change', ({ data }) => {
          if (data?.sectionId !== this.dataset.sectionId) return;

          this.updateVariant(data.variant);
        });
      }

      if (this.layer) {
        document.body.append(this.layer);

        this.trigger.addEventListener(
          'click',
          () => this.open(),
          options
        );

        this.closeButton.addEventListener(
          'click',
          () => this.close(),
          options
        );

        this.layer
          .querySelector('[data-payment-backdrop]')
          .addEventListener(
            'click',
            () => this.close(),
            options
          );

        this.panel.addEventListener(
          'keydown',
          (event) => {
            if (
              event.key !== 'Escape' ||
              event.defaultPrevented ||
              this.providerDialogIsOpen()
            ) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();

            this.close();
          },
          options
        );

        this.layer
          .querySelector('[data-focus-start]')
          .addEventListener(
            'focus',
            () => {
              const focusables = this.focusables(this.panel);

              (
                focusables[focusables.length - 1] ||
                this.closeButton
              ).focus();
            },
            options
          );

        this.layer
          .querySelector('[data-focus-end]')
          .addEventListener(
            'focus',
            () => this.closeButton.focus(),
            options
          );

        document.addEventListener(
          'focusin',
          () => {
            if (!this.isOpen) return;

            const providerOpen =
              this.providerDialogIsOpen();

            this.panel.setAttribute(
              'aria-modal',
              providerOpen ? 'false' : 'true'
            );

            if (
              !providerOpen &&
              this.panel.contains(document.activeElement)
            ) {
              document.body.style.overflow = 'hidden';
              document.documentElement.style.overflow = 'hidden';
            }
          },
          options
        );

        /*
         * Warm Afterpay only after real customer interaction.
         *
         * This keeps Square/Afterpay completely out of the initial
         * page load and Lighthouse/PageSpeed run, while allowing
         * the provider to initialize before the customer opens
         * the payment drawer.
         */
        const warmAfterpay = () => {
          if (
            !this.isConnected ||
            !this.variant?.available ||
            !this.layer?.querySelector('square-placement')
          ) {
            return;
          }

          loadAfterpay().catch(() => {});
        };

        /*
         * Any real pointer or keyboard interaction can warm
         * Afterpay once in the background.
         */
        document.addEventListener(
          'pointerdown',
          warmAfterpay,
          {
            ...options,
            once: true,
            passive: true,
          }
        );

        document.addEventListener(
          'keydown',
          warmAfterpay,
          {
            ...options,
            once: true,
          }
        );

        /*
         * Also warm immediately when the customer shows direct
         * intent toward the payment information.
         */
        this.trigger.addEventListener(
          'pointerenter',
          warmAfterpay,
          options
        );

        this.trigger.addEventListener(
          'focus',
          warmAfterpay,
          options
        );
      }

      this.syncFromForm();
    }

    disconnectedCallback() {
      this.close(false, true);

      this.controller?.abort();
      this.controller = null;

      this.unsubscribe?.();

      this.layer?.remove();
    }

    syncFromForm() {
      const id =
        this.productForm
          ?.querySelector('[name="id"]')
          ?.value;

      this.updateVariant(
        this.variants.find(
          (variant) =>
            String(variant.id) === String(id)
        )
      );
    }

    updateVariant(variant) {
      this.variant = variant;

      const paymentLine =
        this.querySelector('[data-payment-line]');

      if (paymentLine) {
        paymentLine.hidden = !variant?.available;
      }

      if (!variant?.available) {
        this.close();
        return;
      }

      if (this.layer) {
        const input =
          this.layer.querySelector(
            '.brl-payment-form input[name="id"]'
          );

        if (
          input &&
          input.value !== String(variant.id)
        ) {
          input.value = variant.id;

          input.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        }

        const price = Number(variant.price);

        const placement =
          this.layer.querySelector('square-placement');

        const afterpayAvailable =
          placement &&
          placement.getAttribute('data-is-eligible') !==
            'false';

        const shopPayAvailable =
          this.layer.querySelector(
            'shopify-payment-terms'
          );

        const installments =
          afterpayAvailable || price >= 5000 ? 4 : 2;

        const eligibleAmount =
          this.dataset.showAmount === 'true' &&
          price > 0 &&
          price < 100000 &&
          (
            afterpayAvailable ||
            (
              shopPayAvailable &&
              price >= 3500
            )
          ) &&
          !this.productForm?.querySelector(
            '[name="selling_plan"]'
          )?.value;

        this.querySelector(
          '[data-payment-prefix]'
        ).hidden = !eligibleAmount;

        this.querySelector(
          '[data-payment-suffix]'
        ).hidden = !eligibleAmount;

        this.querySelector(
          '[data-payment-amount]'
        ).hidden = !eligibleAmount;

        this.querySelector(
          '[data-payment-generic]'
        ).hidden = eligibleAmount;

        if (eligibleAmount) {
          const amount = money(
            Math.ceil(price / installments)
          );

          this.querySelector(
            '[data-payment-amount]'
          ).textContent = amount;

          this.trigger.setAttribute(
            'aria-label',
            this.dataset.openLabel.replace(
              '[amount]',
              amount
            )
          );
        } else {
          this.trigger.removeAttribute(
            'aria-label'
          );
        }

        if (placement) {
          placement.setAttribute(
            'data-item-skus',
            String(variant.id)
          );

          placement.setAttribute(
            'data-amount',
            (price / 100).toFixed(2)
          );
        }
      }
    }

    focusables(root) {
      const result = [];

      for (
        const element of root.querySelectorAll('*')
      ) {
        if (element.shadowRoot) {
          result.push(
            ...this.focusables(element.shadowRoot)
          );
        }

        if (
          element.matches(
            'button, a[href], input, select, textarea, [tabindex]'
          ) &&
          element.tabIndex >= 0 &&
          !element.disabled &&
          !element.matches(
            '[data-focus-start], [data-focus-end]'
          ) &&
          visible(element)
        ) {
          result.push(element);
        }
      }

      return result;
    }

    providerDialogIsOpen() {
      const visit = (root) => {
        for (
          const element of root.querySelectorAll('*')
        ) {
          if (
            element !== this.panel &&
            element.matches(
              'dialog[open], [role="dialog"][aria-modal="true"]'
            ) &&
            visible(element)
          ) {
            return true;
          }

          if (
            element.shadowRoot &&
            visit(element.shadowRoot)
          ) {
            return true;
          }
        }

        return false;
      };

      return visit(document.body);
    }

    open() {
      if (
        !this.layer ||
        this.isOpen ||
        !this.variant?.available
      ) {
        return;
      }

      clearTimeout(this.closeTimer);

      this.isOpen = true;

      this.savedScroll = {
        x: window.scrollX,
        y: window.scrollY,
      };

      this.savedBodyStyles =
        Object.fromEntries(
          [
            'position',
            'top',
            'left',
            'width',
          ].map((property) => [
            property,
            document.body.style[property],
          ])
        );

      this.savedOverflow = {
        body: document.body.style.overflow,
        html: document.documentElement.style.overflow,
      };

      document.body.setAttribute(
        'data-brl-payment-scroll-lock',
        ''
      );

      document.body.style.position = 'fixed';
      document.body.style.top =
        `${-this.savedScroll.y}px`;
      document.body.style.left =
        `${-this.savedScroll.x}px`;
      document.body.style.width = '100%';

      this.layer.hidden = false;

      this.panel.removeAttribute('inert');

      this.panel.setAttribute(
        'aria-modal',
        'true'
      );

      this.trigger.setAttribute(
        'aria-expanded',
        'true'
      );

      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow =
        'hidden';

      requestAnimationFrame(() => {
        if (!this.isOpen) return;

        this.layer.classList.add('is-open');

        this.closeButton.focus({
          preventScroll: true,
        });
      });

      if (
        this.layer.querySelector(
          'square-placement'
        )
      ) {
        const status =
          this.layer.querySelector(
            '[data-afterpay-status]'
          );

        loadAfterpay()
          .then(() => {
            status.hidden = true;
          })
          .catch(() => {
            status.textContent =
              this.dataset.paymentUnavailable;
          });
      }
    }

    close(
      restoreFocus = true,
      immediate = false
    ) {
      if (!this.layer) return;

      clearTimeout(this.closeTimer);

      if (this.isOpen) {
        this.isOpen = false;

        this.layer.classList.remove('is-open');

        this.panel.setAttribute(
          'inert',
          ''
        );

        this.trigger.setAttribute(
          'aria-expanded',
          'false'
        );

        Object.assign(
          document.body.style,
          this.savedBodyStyles
        );

        document.body.style.overflow =
          this.savedOverflow?.body || '';

        document.documentElement.style.overflow =
          this.savedOverflow?.html || '';

        if (restoreFocus) {
          (
            !this.variant?.available
              ? this.productForm?.querySelector(
                  '[type="submit"]'
                )
              : this.trigger
          )?.focus({
            preventScroll: true,
          });
        }

        const scroll = this.savedScroll;

        window.scrollTo({
          left: scroll.x,
          top: scroll.y,
          behavior: 'instant',
        });

        document.body.removeAttribute(
          'data-brl-payment-scroll-lock'
        );

        requestAnimationFrame(() => {
          if (
            !this.isOpen &&
            this.savedScroll === scroll
          ) {
            window.scrollTo({
              left: scroll.x,
              top: scroll.y,
              behavior: 'instant',
            });
          }
        });
      }

      if (immediate) {
        this.layer.hidden = true;
      } else {
        this.closeTimer = setTimeout(() => {
          this.layer.hidden = true;
        }, 300);
      }
    }
  }

  customElements.define(
    'brl-product-services',
    BrlProductServices
  );
})();